import {
  deleteDoc, doc, getDoc, setDoc, updateDoc,
} from 'firebase/firestore';
import firebase from '../firebase';
import getRequestMeta from '../functions/generateMeta';

import { ip } from '../functions/urls';

const getToken = async (u) => u.getIdToken();

class Doc {
  /**
   * @constructor
   * >### Encapsulates firestore "doc" function for compatibility
   * * * *
   * Gets a `DocumentReference` instance that refers to the document at the
   * specified absolute or relative path. If no path is specified,
   * an automatically-generated unique ID will be used for the returned `DocumentReference`.
   * @param {string} [path] A slash-separated path to a document.
   * Has to be omitted to use auto-genrated IDs.
   * @param {CollectionReference<any>} [ref] A reference to a collection.
   * @throws If the final path has an odd number of segments and does not point to a document.
   * @returns The `Doc` instance.
   */
  constructor(path, ref) {
    if (!ref) {
      this.ref = doc(firebase.db, path);
    } else if (path) {
      this.ref = doc(ref, path);
    } else {
      this.ref = doc(ref);
    }
    this.id = this.ref.id;
    this.data = this.ref.data;
  }

  /**
   * >### Wrapper of firestore `getDoc` function
   * * * *
   * Reads the document referred to by this `DocumentReference`.
   * Note: `getDoc()` attempts to provide up-to-date data when possible
   * by waiting for data from the server, but it may return cached data
   * or fail if you are offline and the server cannot be reached.
   * To specify this behavior, invoke `getDocFromCache` or `getDocFromServer`.
   * @returns A `Promise` resolved with a `DocumentSnapshot`
   * containing the current document contents.
   */
  get() {
    const tryGet = async () => {
      try {
        return await getDoc(this.ref);
      } catch (error) {
        console.error(`Failed to get document: ${error.message}`);
        if (!firebase.auth.currentUser) {
          throw new Error('User is not authenticated');
        }
        const token = await getToken(firebase.auth.currentUser);
        const opt = {
          ...await getRequestMeta(token, 'POST', 'JSON'),
          body: JSON.stringify({
            method: 'get',
            path: this.ref.path,
          }),
        };
        try {
          const resFetch = await fetch(`${ip}/database/document`, opt);

          if (!resFetch.ok) {
            throw new Error(`Failed to fetch document: ${resFetch.statusText}`);
          }
          const json = await resFetch.json();
          const { data, exists } = json.info;
          const docSnapshot = {
            id: this.id,
            data: () => data,
            exists: () => exists,
            get: (param) => data?.[param],
            ref: this.ref,
          };
          return docSnapshot;
        } catch (err) {
          throw new Error(`Failed to fetch document: ${err.message}`);
        }
      }
    };

    return tryGet();
  }

  /**
   * >### Wrapper of firestore `setDoc` function
   * * * *
   * Writes to the document referred to by the specified `DocumentReference`.
   * If the document does not yet exist, it will be created. If you provide `merge`
   * or `mergeFields`, the provided data can be merged into an existing document.
   * @param {object} data A map of the fields and values for the document.
   * @param {object} options An object to configure the set behavior.
   * @returns A `Promise` resolved once the data has been successfully written to the backend
   * (note that it won't resolve while you're offline).
  */
  set(data, options = {}) {
    const trySet = async () => {
      try {
        return await setDoc(this.ref, data, options);
      } catch (error) {
        console.error(`Failed to set document: ${error.message}`);
        if (!firebase.auth.currentUser) {
          throw new Error('User is not authenticated');
        }
        const token = await getToken(firebase.auth.currentUser);
        const opt = {
          ...await getRequestMeta(token, 'POST', 'JSON'),
          body: JSON.stringify({
            data, options, method: 'post', path: this.ref.path,
          }),
        };
        const resFetch = await fetch(`${ip}/database/document`, opt);
        if (!resFetch.ok) {
          throw new Error(`Failed to set document: ${resFetch.statusText}`);
        }
        const json = await resFetch.json();
        return json;
      }
    };
    return trySet();
  }

  /**
   * >### Wrapper of firestore `updateDoc` function
   * * * *
   * Updates fields in the document referred to by the specified `DocumentReference`.
   * The update will fail if applied to a document that does not exist.
   * @param {object} data An object containing the fields and values with which to update
   * the document. Fields can contain dots to reference nested fields within the document.
   * @returns A `Promise` resolved once the data has been successfully written to the backend
   * (note that it won't resolve while you're offline).
   */
  update(data) {
    const tryUpdate = async () => {
      try {
        return await updateDoc(this.ref, data);
      } catch (error) {
        console.error(`Failed to update document: ${error.message}`);
        if (!firebase.auth.currentUser) {
          throw new Error('User is not authenticated');
        }
        const token = await getToken(firebase.auth.currentUser);
        const opt = {
          ...await getRequestMeta(token, 'POST', 'JSON'),
          body: JSON.stringify({ data, method: 'put', path: this.ref.path }),
        };
        const resFetch = await fetch(`${ip}/database/document`, opt);
        if (!resFetch.ok) {
          throw new Error(`Failed to update document: ${resFetch.statusText}`);
        }
        const json = await resFetch.json();
        return json;
      }
    };
    return tryUpdate();
  }

  /**
   * >### Wrapper of firestore `deleteDoc` function
   * * * *
   * Deletes the document referred to by the specified `DocumentReference`.
   * @returns A `Promise` resolved once the document has been successfully deleted from the backend
   * (note that it won't resolve while you're offline).
   */
  delete() {
    const tryDelete = async () => {
      try {
        return await deleteDoc(this.ref);
      } catch (error) {
        console.error(`Failed to delete document: ${error.message}`);
        if (!firebase.auth.currentUser) {
          throw new Error('User is not authenticated');
        }
        const token = await getToken(firebase.auth.currentUser);
        const opt = {
          ...await getRequestMeta(token, 'POST', 'JSON'),
          body: JSON.stringify({
            method: 'delete',
            path: this.ref.path,
          }),
        };
        const resFetch = await fetch(`${ip}/database/document`, opt);
        if (!resFetch.ok) {
          throw new Error(`Failed to delete document: ${resFetch.statusText}`);
        }
        const json = await resFetch.json();
        return json;
      }
    };
    return tryDelete();
  }
}

export default Doc;
